import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useNews, useGalleryItems } from '../hooks/useSupabase';
import { useSite } from '../contexts/SiteContext';
import SafeIcon from '../common/SafeIcon';
import EditableContent from '../components/EditableContent';
import ShareButton from '../components/ShareButton';
import YouTubeChannelEmbed from '../components/YouTubeChannelEmbed';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiExternalLink, FiImage, FiPlay, FiInstagram, FiFacebook } = FiIcons;

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('el-GR', options);
};

const articleImage = (article) => article.image || article.image_url || '';
const isMatchdayPost = (article) => Boolean(article.matchday_post_key);

const NewsPage = () => {
  const [activeTab, setActiveTab] = useState('news');
  const { data: newsItems, loading: newsLoading } = useNews();
  const { data: galleryData, loading: galleryLoading } = useGalleryItems();
  const { siteConfig } = useSite();

  const facebookUrl = siteConfig?.social_facebook || 'https://www.facebook.com/aoxaniawfc';
  const instagramUrl = siteConfig?.social_instagram || 'https://www.instagram.com/aoxaniawfc';
  const youtubeUrl = siteConfig?.social_youtube || 'https://www.youtube.com/@AOXaniawfc';
  const facebookEmbedUrl = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(facebookUrl)}&tabs=timeline&width=500&height=680&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`;

  useEffect(() => {
    if (activeTab !== 'social') return undefined;
    const process = () => window.instgrm?.Embeds?.process?.();
    const existing = document.querySelector('script[data-aox-instagram-embed="true"]');
    if (existing) {
      if (window.instgrm?.Embeds?.process) process();
      else existing.addEventListener('load', process, { once: true });
      return () => existing.removeEventListener('load', process);
    }
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    script.defer = true;
    script.dataset.aoxInstagramEmbed = 'true';
    script.addEventListener('load', process, { once: true });
    document.body.appendChild(script);
    return () => script.removeEventListener('load', process);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-primary-700 text-white py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <EditableContent page="news" section="header" contentKey="title" defaultValue="Νέα & Media" tag="span" />
          </h1>
          <p className="text-xl text-primary-200">
            <EditableContent page="news" section="header" contentKey="subtitle" defaultValue="Μείνετε ενημερωμένοι για όλες τις εξελίξεις της ομάδας μας" tag="span" />
          </p>
        </div>
        <div className="absolute top-4 right-4">
          <ShareButton title="Νέα & Media - Α.Ο. Χανιά" description="Μείνετε ενημερωμένοι για όλες τις εξελίξεις της ομάδας μας" compact={true} variant="outline" className="bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30" />
        </div>
      </section>

      <section className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {[['news','Νέα & Ανακοινώσεις'],['gallery','Φωτογραφίες & Βίντεο'],['social','Social Media']].map(([key,label]) => (
              <button key={key} onClick={() => setActiveTab(key)} className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === key ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
            ))}
          </div>
        </div>
      </section>

      {activeTab === 'news' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {newsLoading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto" /></div>
            ) : newsItems?.length ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                  <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-xl font-black text-gray-900">Όλες οι δημοσιεύσεις</h2><span className="text-sm font-semibold text-slate-500">{newsItems.length} δημοσιεύσεις · Νεότερες πρώτα</span></div>
                  {newsItems.map((article, index) => (
                    <motion.article key={article.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: Math.min(index * 0.035, 0.25) }} className="bg-white rounded-xl shadow-lg overflow-hidden relative group">
                      {articleImage(article) && (
                        <Link to={`/news/${article.id}`} className={`block ${isMatchdayPost(article) ? 'bg-slate-100' : 'bg-gray-100'} overflow-hidden`} aria-label={`Άνοιγμα: ${article.title}`}>
                          <img src={articleImage(article)} alt={article.title} className={isMatchdayPost(article) ? 'w-full max-h-[440px] object-contain transition-transform duration-200 group-hover:scale-[1.01]' : 'w-full h-64 object-cover transition-transform duration-200 group-hover:scale-[1.02]'} />
                        </Link>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-4 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${article.category === 'Αγώνας' || article.category === 'Αγώνες' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{article.category}</span>
                          <div className="flex items-center text-gray-500 text-sm"><SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />{formatDate(article.published_at)}</div>
                        </div>
                        <Link to={`/news/${article.id}`} className="block group/title">
                          <h2 className="text-2xl font-bold text-gray-900 mb-4 leading-tight group-hover/title:text-primary-700 transition-colors">{article.title}</h2>
                        </Link>
                        <p className="text-gray-700 mb-5 leading-relaxed whitespace-pre-wrap">{(article.excerpt || article.content || '').slice(0, 340)}{(article.excerpt || article.content || '').length > 340 ? '…' : ''}</p>
                        <Link to={`/news/${article.id}`} className="inline-flex font-bold text-primary-700 hover:underline">Διαβάστε την ανάρτηση →</Link>
                      </div>
                      <div className="absolute top-4 right-4">
                        <ShareButton title={article.title} description={article.excerpt || `${article.content || ''}`.substring(0, 100)} imageUrl={articleImage(article)} compact={true} variant="outline" className="bg-white/80 backdrop-blur-sm" />
                      </div>
                    </motion.article>
                  ))}
                </div>

                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Γρήγορη πρόσβαση</h3>
                    <div className="space-y-4">
                      {newsItems.slice(0, 5).map((article) => (
                        <div key={article.id} className="border-b border-gray-200 pb-4 last:border-b-0 relative pr-9">
                          <Link to={`/news/${article.id}`} className="block hover:text-primary-700">
                            <h4 className="font-semibold text-gray-900 hover:text-primary-700 mb-2 text-sm leading-tight">{article.title}</h4>
                            <div className="flex items-center text-gray-500 text-xs"><SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />{formatDate(article.published_at)}</div>
                          </Link>
                          <div className="absolute top-0 right-0"><ShareButton title={article.title} description={article.excerpt || `${article.content || ''}`.substring(0, 100)} imageUrl={articleImage(article)} compact={true} variant="text" iconSize="sm" /></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-primary-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Γρήγοροι Σύνδεσμοι</h3>
                    <div className="space-y-3">
                      <Link to="/matches" className="flex items-center text-primary-600 hover:text-primary-700"><SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-2" /><span className="text-sm">Πρόγραμμα Αγώνων</span></Link>
                      <Link to="/team" className="flex items-center text-primary-600 hover:text-primary-700"><SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-2" /><span className="text-sm">Η Ομάδα</span></Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-lg"><p className="text-gray-600">Δεν υπάρχουν διαθέσιμα νέα</p></div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'gallery' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><h2 className="text-3xl font-bold text-gray-900 mb-4">Φωτογραφίες & Βίντεο</h2><p className="text-xl text-gray-600">Δείτε τις καλύτερες στιγμές της ομάδας μας</p></div>
            <div className="mb-12"><YouTubeChannelEmbed channelUrl={youtubeUrl} title="Τελευταία Videos από το YouTube" description="Τα τελευταία uploads από το επίσημο κανάλι του ΑΟ Χανιά WFC εμφανίζονται αυτόματα εδώ." /></div>
            {galleryLoading ? <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto" /></div> : galleryData?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleryData.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden group relative">
                    <div className="relative"><img src={item.url} alt={item.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-200" /><div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center bg-blue-500"><SafeIcon icon={item.type === 'video' ? FiPlay : FiImage} className="w-4 h-4 text-white" /></div><div className="absolute top-4 right-4"><ShareButton title={`${item.title} - Gallery ΑΟΧ`} description={item.title} imageUrl={item.url} compact={true} variant="outline" className="bg-white/80" /></div></div>
                    <div className="p-4"><h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3><div className="flex items-center text-gray-500 text-sm"><SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />{formatDate(item.date)}</div></div>
                  </div>
                ))}
              </div>
            ) : <div className="text-center py-12 bg-white rounded-xl shadow-lg"><p className="text-gray-600">Δεν υπάρχουν διαθέσιμα πολυμέσα</p></div>}
          </div>
        </section>
      )}

      {activeTab === 'social' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><h2 className="text-3xl font-bold text-gray-900 mb-4">Social Media</h2><p className="text-xl text-gray-600">Ακολουθήστε μας στα social media για live updates</p></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-6"><SafeIcon icon={FiInstagram} className="w-8 h-8 text-pink-600" /><div><h3 className="text-xl font-bold text-gray-900">Instagram</h3><p className="text-gray-600">@aoxaniawfc</p></div></div>
                <div className="bg-gray-50 rounded-lg p-4 text-center"><a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg"><SafeIcon icon={FiInstagram} className="w-4 h-4 mr-2" />Άνοιγμα στο Instagram</a></div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-6"><SafeIcon icon={FiFacebook} className="w-8 h-8 text-blue-600" /><div><h3 className="text-xl font-bold text-gray-900">Facebook</h3><p className="text-gray-600">AO Chania Women's FC</p></div></div>
                <div className="bg-gray-50 rounded-lg p-4"><div className="flex justify-center overflow-hidden rounded-lg bg-white border border-gray-200"><iframe title="AO Chania Women's FC Facebook Page" src={facebookEmbedUrl} width="500" height="680" style={{ border: 'none', overflow: 'hidden', width: '100%', maxWidth: '500px' }} scrolling="no" frameBorder="0" allowFullScreen={true} allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" loading="lazy" referrerPolicy="strict-origin-when-cross-origin" /></div><div className="text-center mt-4"><a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"><SafeIcon icon={FiFacebook} className="w-4 h-4 mr-2" />Άνοιγμα στο Facebook</a></div></div>
              </div>
            </div>
            <div className="mt-8"><YouTubeChannelEmbed channelUrl={youtubeUrl} title="YouTube - AO Xania WFC" description="Παρακολουθήστε τα τελευταία videos, αγώνες και στιγμές της ομάδας από το επίσημο κανάλι μας." /></div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsPage;
